/**
 * Firestore shape for entries in the per-section change-log subcollection
 * at `sections/{sectionId}/sectionDocumentLogs/{autoId}`. Written by the
 * `logsectiononwrite` Cloud Function (and any future writers) whenever the
 * parent section document changes, so admins can audit the field-level
 * history of a section.
 *
 * Pure shape + path helpers only. SDK refs and the admin-side write helper
 * live in `../../admin/sectionHistoryLogReferences` so this module stays
 * usable from any environment (client, admin, pure code).
 */
import {z} from 'zod';

import type {SectionId} from '../../../section/sectionApi';
import {FirestoreTimestampSchema, type FirebaseTimestamp} from '../../types';
import {SectionDocumentSchema, type SectionDocument} from './sectionDocument';

/**
 * Firestore shape for `sections/{sectionId}/sectionDocumentLogs/{autoId}`.
 *
 * Each log captures the full before/after section state plus a timestamp,
 * and is keyed by an auto-id so logs are append-only.
 */
export interface SectionHistoryLog {
  /**
   * The section document before the change, or `null` if the section did
   * not exist before the change (i.e. this log records the create).
   */
  beforeChange: SectionDocument | null;
  /**
   * The section document after the change, or `null` if the section was
   * deleted.
   */
  afterChange: SectionDocument | null;
  /**
   * The timestamp of the change. Modelled with the {@link FirebaseTimestamp}
   * structural interface so both the client and admin SDK `Timestamp`
   * classes assign to it without this package having to import either SDK.
   */
  changedAt: FirebaseTimestamp;
}

/**
 * Runtime validator for {@link SectionHistoryLog}. `.loose()` so logs
 * written by future schema versions still parse.
 */
export const SectionHistoryLogSchema = z
  .object({
    beforeChange: SectionDocumentSchema.nullable(),
    afterChange: SectionDocumentSchema.nullable(),
    changedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Name of the per-section history-log subcollection. */
export const SECTION_DOCUMENT_LOGS_SUBCOLLECTION_NAME = 'sectionDocumentLogs';

/**
 * Builds the Firestore path for the history-log subcollection of a
 * section.
 *
 * @param sectionId - The parent section id.
 * @returns The path
 *     `sections/{sectionId}/${SECTION_DOCUMENT_LOGS_SUBCOLLECTION_NAME}`.
 */
export function getSectionDocumentLogsCollectionFirebasePath(
  sectionId: SectionId | string,
): string {
  return `sections/${sectionId}/${SECTION_DOCUMENT_LOGS_SUBCOLLECTION_NAME}`;
}
